import { FunctionComponent, memo, forwardRef } from 'react';

import styled, { css } from 'styled-components';

import { color } from '../../helpers/style.helpers';

import {
  SelectSkeletonMenuPosition,
  SelectSkeletonMenuProps,
  SELECT_SKELETON_MENU_POSITIONS,
  SelectSkeletonMenuGeometry,
} from './SelectSkeleton.decl';
import { SelectSkeletonItem } from './SelectSkeletonItem';

import { motion, MotionProps, Variants } from 'framer-motion';
import get from 'lodash-es/get';
import groupBy from 'lodash-es/groupBy';

type MotionBoxProps = MotionProps & { backgroundColor?: string };

const animationVariants: Variants = {
  entry: {
    y: '0%',
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
  exit: {
    y: '-25%',
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: 'easeInOut',
    },
  },
};

const getAnimationVariant = (menuPosition?: SelectSkeletonMenuPosition) => {
  if (menuPosition !== SELECT_SKELETON_MENU_POSITIONS.TOP) {
    return animationVariants;
  }

  return {
    entry: {
      ...animationVariants.entry,
      y: '-100%',
    },
    exit: {
      ...animationVariants.exit,
      y: '-75%',
    },
  };
};

const StyledListContainer = styled.ul<{ grouped: boolean; index: number }>`
  padding: 0;
  margin: 0;
  ${({ grouped, index }) =>
    grouped &&
    index > 0 &&
    css`
      border-top: 1px solid ${color.grey.light};
    `}
`;
const MotionBox = styled<FunctionComponent<MotionBoxProps & SelectSkeletonMenuGeometry>>(
  motion.div
)`
  position: fixed;

  /** Prevent the select menu being hidden in a dropdown */
  z-index: 1020;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  height: max-content;
  max-height: ${({ maxHeight }) => maxHeight}px;
  padding: 0;
  border: 1px solid ${color.grey.dark};
  border-radius: ${({ menuPosition }) =>
    menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP ? '8px 8px 0 0' : '0 0 8px 8px'};
  border-top: ${({ menuPosition }) =>
    menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP ? undefined : 0};
  border-bottom: ${({ menuPosition }) =>
    menuPosition === SELECT_SKELETON_MENU_POSITIONS.TOP ? 0 : undefined};
  margin: 0;
  background-color: ${({ backgroundColor }) => backgroundColor};
  overflow-x: hidden;
  overflow-y: auto;
`;

export const SelectSkeletonMenu = memo(
  forwardRef<HTMLDivElement, SelectSkeletonMenuProps>(
    (
      {
        options,
        backgroundColor,
        width,
        top,
        left,
        menuPosition,
        defaultSelected,
        onChange,
        label,
        name,
        disabled,
        placeholder,
        size,
        optionRenderer,
        isOpen,
        selectedOption,
        disableItemAutofocus,
        menuComponent: MenuComponent,
        maxHeight,
        'data-test': dataTest,
      },
      ref
    ) => {
      const groupedOptions = groupBy(options, (option) => get(option, 'group', 'ungrouped'));
      const groups = Object.keys(groupedOptions);
      return (
        <div ref={ref}>
          <MotionBox
            maxHeight={maxHeight}
            variants={getAnimationVariant(menuPosition)}
            initial='exit'
            animate='entry'
            exit='exit'
            backgroundColor={backgroundColor}
            data-test={`${dataTest}-menu`}
            width={width}
            top={top}
            left={left}
            menuPosition={menuPosition}
          >
            <MenuComponent
              options={options}
              backgroundColor={backgroundColor}
              menuPosition={menuPosition}
              disableItemAutofocus={disableItemAutofocus}
              data-test={dataTest}
              defaultSelected={defaultSelected}
              onChange={onChange}
              label={label}
              name={name}
              disabled={disabled}
              placeholder={placeholder}
              size={size}
              optionRenderer={optionRenderer}
              isOpen={isOpen}
              selectedOption={selectedOption}
            >
              {groups.map((group, groupIndex) => (
                <StyledListContainer
                  key={group}
                  index={groupIndex}
                  grouped={groups.length > 1}
                  data-test={`${dataTest}-${group}`}
                >
                  {groupedOptions[group].map((option, index) => (
                    <SelectSkeletonItem
                      grouped={groups.length > 1}
                      key={option.value.toString()}
                      option={option}
                      index={index}
                      data-test={`${dataTest}-item`}
                      disableAutofocus={disableItemAutofocus}
                    />
                  ))}
                </StyledListContainer>
              ))}
            </MenuComponent>
          </MotionBox>
        </div>
      );
    }
  )
);
